import { formatCurrency } from '@repo/ui/lib/format/format-currency';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useIsMobile } from '@repo/ui/hooks/use-mobile';
import { useSearchParams } from 'react-router';
import { cn } from '@/lib/utils';
import { DateFormat, formatDate } from '@repo/ui/lib/format/format-date';

type DailyStat = {
  date: string;
  amount: number;
};

interface SalesDailySummaryProps {
  daySummary: DailyStat[];
}

export function SalesDailySummary({ daySummary }: SalesDailySummaryProps) {
  const isMobile = useIsMobile();
  const [startIndex, setStartIndex] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const dayValue = searchParams.get('day');

  const windowSize = isMobile ? 3 : 7;
  const step = isMobile ? 1 : 7;

  useEffect(() => {
    if (daySummary.length === 0) {
      setStartIndex(0);
      return;
    }

    const lastIndex = daySummary.length - 1;
    const initialStart = Math.max(0, lastIndex - windowSize + 1);
    setStartIndex(initialStart);
  }, [daySummary, windowSize]);

  useEffect(() => {
    if (!dayValue || daySummary.length === 0) return;

    const selectedIndex = daySummary.findIndex((day) => day.date === dayValue);
    if (selectedIndex === -1) return;

    const windowStart = startIndex;
    const windowEnd = startIndex + windowSize - 1;
    const isVisible = selectedIndex >= windowStart && selectedIndex <= windowEnd;
    if (isVisible) return;

    const centeredStart = selectedIndex - Math.floor(windowSize / 2);
    const nextStart = Math.min(
      Math.max(0, centeredStart),
      Math.max(0, daySummary.length - windowSize)
    );

    setStartIndex(nextStart);
  }, [dayValue, daySummary, startIndex, windowSize]);

  const visibleStats = useMemo(() => {
    if (daySummary.length === 0) return [];
    return daySummary.slice(startIndex, startIndex + windowSize);
  }, [daySummary, startIndex, windowSize]);

  const canGoPrev = startIndex > 0;
  const canGoNext = startIndex + windowSize < daySummary.length;

  const handlePrev = () => {
    if (!canGoPrev) return;
    setStartIndex((prev) => Math.max(0, prev - step));
  };

  const handleNext = () => {
    if (!canGoNext) return;
    setStartIndex((prev) => Math.min(daySummary.length - windowSize, prev + step));
  };

  if (daySummary.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={handlePrev}
        disabled={!canGoPrev}
        aria-label="Periodo anterior"
        className="flex items-center justify-center w-8 h-8 rounded-full border border-gray-200 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>

      <div className="flex-1 overflow-x-auto">
        <div className="flex gap-2 min-w-max">
          {visibleStats.map((day) => {
            const isSelected = dayValue === day.date;
            const dateParts = day.date.split('-').map(Number);
            const dateObj = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
            return (
              <button
                key={day.date}
                type="button"
                onClick={() =>
                  setSearchParams((prev) => {
                    if (isSelected) {
                      prev.delete('day');
                      return prev;
                    } else {
                      return new URLSearchParams({
                        day: day.date,
                      });
                    }
                  })
                }
                className={cn(
                  'flex-1 flex flex-col items-center justify-center px-4 py-2 rounded-lg border transition-colors border-gray-200 bg-gray-50 text-gray-900 cursor-pointer hover:bg-gray-50',
                  isSelected ? 'border-gray-300 bg-gray-200' : ''
                )}
              >
                <span className="text-xs text-gray-500">
                  {formatDate(dateObj, DateFormat.DD_MM)}
                </span>
                <span className="text-sm font-semibold">{formatCurrency(day.amount)}</span>
              </button>
            );
          })}
        </div>
      </div>

      <button
        type="button"
        onClick={handleNext}
        disabled={!canGoNext}
        aria-label="Periodo siguiente"
        className="flex items-center justify-center w-8 h-8 rounded-full border border-gray-200 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}
